import "./Dropdown.scss";
import * as R from "ramda";
import React, { useEffect, useMemo, useState } from "react";
import { Dropdown as BSDropdown } from "react-bootstrap";
import { MdClose } from "react-icons/md";
import { Setter, typedReactMemo } from "../../utils/types";
import { DropdownMenu, DropdownMenuProps } from "../DropdownMenu/DropdownMenu";
import { Placement } from "../OverlayTrigger";
import { DropdownToggle } from "./DropdownToggle";

export enum DropdownToggleType {
  FILLED = "filled",
  OUTLINED = "outlined",
  EMPTY = "empty",
  WIDGET_TITLE = "widget-title",
  PAGE_TITLE = "page-title",
}

export interface MenuItem<T> {
  className?: string;
  itemIndex?: number;
  itemOptions?: JSX.Element; // If the page opens a submenu, pass it in here
  label?: string;
  link?: string;
  menuIndex?: number;
  selected?: boolean;
  value: T;
  section?: string;
  leadingContent?: JSX.Element;
  trailingContent?: JSX.Element;
  simpleTooltip?: string;
  simpleTooltipVerticalShift?: string;
  disabled?: boolean; // Currently only supports single select items
}

export interface MultiMenuSections {
  [index: string]: { label: string; displayLabel: boolean; footer?: JSX.Element | null }[];
}

export type DropdownOption<T extends string = string> = T | MenuItem<T>;

interface DropdownProps<T extends string>
  extends Omit<
    DropdownMenuProps<T>,
    "className" | "itemOnClick" | "menuItems" | "size" | "storybook"
  > {
  type?: DropdownToggleType;
  design?: "primary" | "secondary";
  background?: "light" | "dark";
  label?: string | JSX.Element;
  value?: T;
  options: readonly DropdownOption<T>[];
  onChange: Setter<T>;
  leadingIcon?: React.ReactElement;
  size?: "sm" | "lg";
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  overlay?: React.ReactElement;
  overlayPlacement?: Placement;
  show?: boolean;
  setShow?: Setter<boolean>;
  dropdownMenuClassName?: string;
  onToggle?:
    | ((
        isOpen: boolean,
        event: React.SyntheticEvent<BSDropdown, Event>,
        metadata: {
          source: "select" | "click" | "rootClose" | "keydown";
        }
      ) => void)
    | undefined;
  autoFill?: boolean;
  menuRenderOverride?: Record<string, "stringList">; // The key is the menu index. The value is the type of render override. Currently only "stringList" is supported.
  multiMenuSections?: MultiMenuSections; // Allows sections for each menu for multi-menu dropdowns
  menuSectionTitles?: Record<string, JSX.Element>; // Allows titles for each menu for multi-menu dropdowns
  applyDisabled?: boolean; // Disables the apply button
  hideCaret?: boolean; // Hides the caret on the right of the DropdownButton
  clearable?: boolean; // Shows a clear icon to reset the selection
  onClear?: () => void;
}

export const Dropdown = typedReactMemo(
  <T extends string>({
    type = DropdownToggleType.FILLED,
    design = "primary",
    background = "light",
    label,
    value,
    options,
    onChange,
    leadingIcon,
    size = "lg",
    disabled,
    placeholder,
    overlay,
    overlayPlacement = "right center",
    className,
    show: showInput,
    setShow: setShowInput,
    onToggle: onToggleInput,
    autofill,
    // DropdownMenu props
    applyContent,
    applyOnClick,
    applyDisabled,
    cancelContent,
    cancelOnClick,
    filterBarPlaceholder,
    footer,
    section,
    multiMenuSections,
    leadingContent,
    trailingContent,
    simpleTooltip,
    footerOnClick,
    hasFilterBar,
    hasTicks,
    hideApplyOnClick,
    hideCancelOnClick,
    id,
    itemOnClick,
    menuItems,
    multiSelect,
    subTitles,
    title,
    dropdownMenuClassName,
    menuRenderOverride,
    menuSectionTitles,
    hideCaret = false,
    clearable = false,
    onClear,
    ...passthrough
  }: DropdownProps<T>): JSX.Element => {
    const items = useMemo(
      () =>
        options.map(option => {
          if (typeof option === "string") {
            return { label: option, value: option };
          } else {
            return { ...option, label: option.label || option.value };
          }
        }),
      [options]
    );

    const menu = useMemo(() => {
      return (
        <DropdownMenu
          className={`cl-dropdown-menu${dropdownMenuClassName ? ` ${dropdownMenuClassName}` : ""}`}
          menuItems={menuItems ? menuItems : items}
          itemOnClick={(item: MenuItem<T>) => {
            if (onChange) {
              onChange(item.value);
            }
          }}
          applyContent={applyContent}
          applyOnClick={applyOnClick}
          applyDisabled={applyDisabled}
          cancelContent={cancelContent}
          cancelOnClick={cancelOnClick}
          hideCancelOnClick={hideCancelOnClick}
          hideApplyOnClick={hideApplyOnClick}
          filterBarPlaceholder={filterBarPlaceholder}
          footer={footer}
          footerOnClick={footerOnClick}
          hasFilterBar={hasFilterBar}
          hasTicks={hasTicks}
          id={id}
          multiSelect={multiSelect}
          section={section}
          multiMenuSections={multiMenuSections}
          size={size}
          storybook={false}
          subTitles={subTitles}
          title={title}
          menuRenderOverride={menuRenderOverride}
          menuSectionTitles={menuSectionTitles}
          {...passthrough}
        />
      );
    }, [
      applyContent,
      applyOnClick,
      applyDisabled,
      cancelContent,
      cancelOnClick,
      filterBarPlaceholder,
      footer,
      footerOnClick,
      hasFilterBar,
      hasTicks,
      id,
      items,
      menuItems,
      multiSelect,
      onChange,
      passthrough,
      size,
      subTitles,
      title,
      section,
      dropdownMenuClassName,
      menuRenderOverride,
      multiMenuSections,
      menuSectionTitles,
      hideCancelOnClick,
      hideApplyOnClick,
    ]);

    const [show, setShow] = useState<boolean>(false);

    const onToggle = onToggleInput
      ? onToggleInput
      : (
          isOpen: boolean,
          event: React.SyntheticEvent<BSDropdown, Event>,
          metadata: { source: "select" | "click" | "rootClose" | "keydown" }
        ) => {
          if (
            event.currentTarget instanceof HTMLElement &&
            event.currentTarget.classList.contains("disabled")
          ) {
            return;
          }

          if (!R.isNil(setShowInput)) {
            setShowInput(isOpen);
          } else {
            setShow(isOpen);
          }
        };

    useEffect(() => {
      if (autofill && items && items.length === 1 && value !== items[0].value) {
        onChange(items[0].value);
      }
    }, [autofill, items, onChange, value]);

    const dropdown = (
      <BSDropdown className="cl-dropdown" show={R.defaultTo(show, showInput)} onToggle={onToggle}>
        <DropdownToggle
          type={type}
          design={design}
          background={background}
          label={label}
          value={value}
          options={options}
          leadingIcon={leadingIcon}
          size={size}
          disabled={disabled}
          placeholder={placeholder}
          overlay={overlay}
          overlayPlacement={overlayPlacement}
          className={className}
          hideCaret={hideCaret}
        />
        {menu}
      </BSDropdown>
    );

    if (clearable && value && onClear) {
      return (
        <div className="cl-dropdown-wrapper clearable">
          {dropdown}
          <MdClose
            className="dropdown-clear-icon"
            onClick={e => {
              e.stopPropagation();
              onClear();
            }}
          />
        </div>
      );
    }

    return dropdown;
  }
);
